import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import ReferralStatusBadge from '../components/ReferralStatusBadge';
import { referralPortalApi } from '../api/referralPortal.api';
import { toast } from 'sonner';
import CampusRoleShell from '@/components/CampusRoleShell';

/** Doctor clinical inbox — letters after leadership approval. */
export default function DoctorReferralInboxPage() {
  const { tenant } = useTenant();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const primary = tenant?.branding?.primary_color || '#F15A22';

  const load = async () => {
    setLoading(true);
    try {
      setRows(await referralPortalApi.inbox(filter || undefined));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CampusRoleShell
      pageTitle="Referral inbox"
      pageSubtitle="Prepare and issue letters for approved referral requests"
    >
      <div data-testid="doctor-referral-inbox">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {['', 'submitted', 'letter_draft', 'issued'].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                filter === s ? 'border-transparent text-white' : 'border-stone-200 bg-white text-stone-600'
              }`}
              style={filter === s ? { background: primary } : undefined}
            >
              {s ? s.replace('_', ' ') : 'All'}
            </button>
          ))}
          <Button variant="outline" size="sm" className="ml-auto" onClick={load}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
        <p className="mb-4 text-xs text-stone-500">
          Out-station approvals are handled in Management → Referral approvals. Approved requests appear here for letter issue.
        </p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-stone-300 bg-white px-6 py-16 text-center text-stone-500">
            No referral requests in this view.
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => (
              <li key={r.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-stone-900">{r.request_number}</p>
                      <ReferralStatusBadge status={r.status} />
                    </div>
                    <p className="mt-1 text-sm text-stone-600">
                      {r.patient?.name} → {r.seeking?.hospital_clinic || '—'} ({r.seeking?.specialisation})
                    </p>
                    <p className="text-xs text-stone-400">
                      {r.station_type === 'out_station' ? 'Out-station' : 'In-station'}
                      {r.preferred_doctor_name ? ` · Dr ${r.preferred_doctor_name}` : ''}
                    </p>
                  </div>
                  <Link
                    to={`/doctor/referrals/${r.id}`}
                    className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-white"
                    style={{ background: primary }}
                  >
                    Open letter
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </CampusRoleShell>
  );
}
